import { useState, useEffect } from 'react';
import { ForumRoom, ForumTopic } from '../types';
import { listRooms } from '../services/forumService';

interface CreateTopicData {
  roomId: string;
  roomName: string;
  title: string;
  content: string;
}

interface EditTopicData {
  title: string;
}

interface TopicModalProps {
  /** Null = create mode, non-null = edit mode */
  topic: ForumTopic | null;
  /** When set (scoped mode), room selector is hidden and this value is used */
  fixedRoomId?: string;
  fixedRoomName?: string;
  onSave: (data: CreateTopicData | EditTopicData) => Promise<void>;
  onClose: () => void;
}

export default function TopicModal({
  topic,
  fixedRoomId,
  fixedRoomName,
  onSave,
  onClose,
}: TopicModalProps) {
  const isEdit = topic !== null;

  const [rooms, setRooms] = useState<ForumRoom[]>([]);
  const [roomId, setRoomId] = useState(fixedRoomId ?? topic?.roomId ?? '');
  const [title, setTitle] = useState(topic?.title ?? '');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit && !fixedRoomId) {
      listRooms({ includeInactive: false }).then(result => {
        setRooms(result.rooms);
        if (result.rooms.length > 0 && !roomId) {
          setRoomId(result.rooms[0].id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedRoomName = fixedRoomName ?? rooms.find(r => r.id === roomId)?.name ?? '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!isEdit && !content.trim()) {
      setError('Content is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await onSave({ title: title.trim() } as EditTopicData);
      } else {
        await onSave({
          roomId,
          roomName: selectedRoomName,
          title: title.trim(),
          content: content.trim(),
        } as CreateTopicData);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save topic');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal modal-blur fade show"
      style={{ display: 'block' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{isEdit ? 'Edit Topic' : 'Create Topic'}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Room — only shown in create mode */}
              {!isEdit && (
                <div className="mb-3">
                  <label className="form-label">Room</label>
                  {fixedRoomId ? (
                    <input type="text" className="form-control" value={selectedRoomName} readOnly />
                  ) : (
                    <select
                      className="form-select"
                      value={roomId}
                      onChange={e => setRoomId(e.target.value)}
                      required
                    >
                      {rooms.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              {/* Content — only shown in create mode (first post) */}
              {!isEdit && (
                <div className="mb-3">
                  <label className="form-label">Content</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    required
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
